/**
 * © 2026 Desktronic. All rights reserved.
 * Proprietary code. Unauthorized copying, distribution or use
 * without written permission from Desktronic is prohibited.
 * Source: desktronic.lt | desktronic.de | desktronic.se | desktronic.pl | desktronic.com/pl | desktronic.fr | desktronic.co.uk | desktronic.be | desktronic.nl | desktronic.dk | desktronic.com |  desktronic.cz
 */

// sig:dt-a7f3c91e04b2

class DeskA11y {
  static FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), iframe, summary, ' +
    '[contenteditable="true"], [tabindex]:not([tabindex="-1"])';

  static #trapStack = [];
  static #liveRegions = {};
  static #strings = null;

  static getFocusable(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return [];
    return Array.from(root.querySelectorAll(DeskA11y.FOCUSABLE)).filter(
      (el) => !el.closest('[inert], [hidden], [aria-hidden="true"]') && el.getClientRects().length > 0
    );
  }

  static #topTrap() {
    return DeskA11y.#trapStack[DeskA11y.#trapStack.length - 1];
  }

  static #setBackgroundInert(container, on) {
    if (!container || !document.body) return;
    for (const el of document.body.children) {
      if (el.contains(container) || el.tagName === 'SCRIPT' || el.tagName === 'STYLE') continue;
      if (on && !el.inert) {
        el.inert = true;
        el.setAttribute('data-desk-inert', '');
      } else if (!on && el.hasAttribute('data-desk-inert')) {
        el.inert = false;
        el.removeAttribute('data-desk-inert');
      }
    }
  }

  static trapFocus(container, options = {}) {
    if (!(container instanceof Element) || !document.body || !document.body.contains(container)) return () => {};

    const returnTo = options.returnFocusTo || document.activeElement;
    const entry = { container };
    const isTop = () => DeskA11y.#topTrap() === entry;
    const focusFirst = () => {
      const target = DeskA11y.getFocusable(container)[0] || container;
      if (typeof target.focus === 'function') target.focus({ preventScroll: true });
    };

    const onKeydown = (event) => {
      if (!isTop()) return;
      if (event.key === 'Escape' && typeof options.onEscape === 'function') {
        event.preventDefault();
        event.stopPropagation();
        options.onEscape(event);
        return;
      }
      if (event.key !== 'Tab') return;

      const items = DeskA11y.getFocusable(container);
      if (!items.length) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || active === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const onFocusin = (event) => {
      if (!isTop() || container.contains(event.target)) return;
      focusFirst();
    };

    const release = ({ restoreFocus = true } = {}) => {
      const index = DeskA11y.#trapStack.indexOf(entry);
      if (index === -1) return;
      document.removeEventListener('keydown', onKeydown, true);
      document.removeEventListener('focusin', onFocusin, true);
      DeskA11y.#trapStack.splice(index, 1);
      DeskA11y.#setBackgroundInert(container, false);
      const previous = DeskA11y.#topTrap();
      if (previous) DeskA11y.#setBackgroundInert(previous.container, true);
      if (restoreFocus && returnTo instanceof HTMLElement && document.contains(returnTo)) {
        returnTo.focus({ preventScroll: true });
      }
    };

    const previous = DeskA11y.#topTrap();
    if (previous) DeskA11y.#setBackgroundInert(previous.container, false);
    DeskA11y.#trapStack.push(entry);
    DeskA11y.#setBackgroundInert(container, true);
    if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');

    document.addEventListener('keydown', onKeydown, true);
    document.addEventListener('focusin', onFocusin, true);

    requestAnimationFrame(() => {
      if (!isTop() || !document.contains(container)) return;
      let initial = options.initialFocus;
      if (typeof initial === 'string') initial = container.querySelector(initial);
      if (initial instanceof HTMLElement) initial.focus({ preventScroll: true });
      else focusFirst();
    });

    return release;
  }

  static #liveRegion(assertive) {
    const key = assertive ? 'assertive' : 'polite';
    const existing = DeskA11y.#liveRegions[key];
    if (existing && document.body.contains(existing)) return existing;

    const el = document.createElement('div');
    el.className = 'visually-hidden';
    el.setAttribute('role', assertive ? 'alert' : 'status');
    el.setAttribute('aria-live', key);
    el.setAttribute('aria-atomic', 'true');
    document.body.appendChild(el);
    DeskA11y.#liveRegions[key] = el;
    return el;
  }

  static announce(message, { assertive = false } = {}) {
    if (!message || !document.body) return;
    const el = DeskA11y.#liveRegion(assertive);
    el.textContent = '';
    setTimeout(() => {
      el.textContent = message;
    }, 100);
  }

  static t(key) {
    if (!DeskA11y.#strings) {
      const node = document.getElementById('DeskA11yStrings');
      try {
        DeskA11y.#strings = node ? JSON.parse(node.textContent) : {};
      } catch {
        DeskA11y.#strings = {};
      }
    }
    return DeskA11y.#strings[key] || '';
  }

  static prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  static #swiperPlaceholders(text) {
    return text.replace(/__INDEX__/g, '{{index}}').replace(/__LENGTH__/g, '{{slidesLength}}');
  }

  static swiperA11y(overrides = {}) {
    return {
      enabled: true,
      prevSlideMessage: DeskA11y.t('slideshow_previous'),
      nextSlideMessage: DeskA11y.t('slideshow_next'),
      firstSlideMessage: DeskA11y.t('slideshow_first'),
      lastSlideMessage: DeskA11y.t('slideshow_last'),
      paginationBulletMessage: DeskA11y.#swiperPlaceholders(DeskA11y.t('slideshow_go_to')),
      slideLabelMessage: DeskA11y.#swiperPlaceholders(DeskA11y.t('slide_status')),
      containerRoleDescriptionMessage: DeskA11y.t('carousel'),
      itemRoleDescriptionMessage: DeskA11y.t('slide'),
      slideRole: 'group',
      ...overrides,
    };
  }
}

window.DeskA11y = DeskA11y;
